import React, { useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import useApiEp from "../../hooks/useApiEp";
import MuPb from "../../widgets/MuPb";
import {
  Box,
  Grid,
  Typography,
  Chip,
  TextField,
  Card,
  CardContent,
  InputAdornment,
  Stack,
  Button,
  Autocomplete,
  Alert,
} from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RTooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  BarChart,
  Bar,
} from "recharts";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import PhoneIcon from "@mui/icons-material/Phone";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import FilterListIcon from "@mui/icons-material/FilterList";
import * as XLSX from "xlsx";
import { API_ENDPOINT } from "../../utils/constants";
import { getToken, getErrorResponse } from "../../utils/util";
import VoiceCallLogsTable, { T } from "./VoiceCallLogsTable";
import {
  VoiceCardStyle,
  VoiceTooltipStyle,
  VoiceReportStatCard,
  fmtDuration,
} from "./VoiceFullReports"; // adjust path if these aren't exported from there
import BackBtn from "../../widgets/BackBtn";

const fmtDur = (s) => {
  const n = parseInt(s, 10);
  if (!n || isNaN(n)) return "";
  return n < 60 ? `${n}s` : `${Math.floor(n / 60)}m ${n % 60}s`;
};

const ScheduledVoiceReport = () => {
  const { id } = useParams();
  const [filters, setFilters] = useState({
    answer_status: "",
    dtmf: "",
    startDate: "",
    endDate: "",
    search: "",
  });
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  const buildQS = useCallback(
    (extra = {}) => {
      const p = new URLSearchParams();
      Object.entries({ ...filters, ...extra }).forEach(
        ([k, v]) => v && p.set(k, v)
      );
      return p.toString();
    },
    [filters]
  );

  const { data, isLoading, isError, error } = useApiEp(
    `/voice/get-scheduled-voice-report/${id}?${buildQS({ page, limit: 15 })}`
  );

  const setF = (k, v) => {
    setFilters((p) => ({ ...p, [k]: v }));
    setPage(1);
  };
  const clearFilters = () => {
    setFilters({
      answer_status: "",
      dtmf: "",
      startDate: "",
      endDate: "",
      search: "",
    });
    setPage(1);
  };
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  if (isLoading) {
    return <MuPb />;
  }
  if (isError) {
    return <Alert severity="error">{getErrorResponse(error)}</Alert>;
  }

  const stats = data || {};
  const list = stats.list?.data || [];
  const campaignNames = stats.campaigns || [];

  const pieData = [
    { name: "Answered", value: stats.answeredCalls || 0, color: T.green },
    { name: "Not Answered", value: stats.notAnsweredCalls || 0, color: T.red },
  ];

  const statCards = [
    {
      label: "Total Calls",
      value: stats.totalCalls,
      icon: <PhoneIcon sx={{ fontSize: 20 }} />,
      color: T.blue,
      bg: T.blueBg,
      border: T.blueBorder,
    },
    {
      label: "Answered",
      value: stats.answeredCalls,
      icon: <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />,
      color: T.green,
      bg: T.greenBg,
      border: T.greenBorder,
    },
    {
      label: "Not Answered",
      value: stats.notAnsweredCalls,
      icon: <HighlightOffIcon sx={{ fontSize: 20 }} />,
      color: T.red,
      bg: T.redBg,
      border: T.redBorder,
    },
    {
      label: "Answer Rate",
      value: stats.answerRate != null ? `${stats.answerRate}%` : "—",
      icon: <TrendingUpIcon sx={{ fontSize: 20 }} />,
      color: T.purple,
      bg: T.purpleBg,
      border: T.purpleBorder,
    },
    {
      label: "Avg Duration",
      value: fmtDuration(stats.avgDuration),
      icon: <TimerOutlinedIcon sx={{ fontSize: 20 }} />,
      color: T.amber,
      bg: T.amberBg,
      border: T.amberBorder,
    },
  ];

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      background: "#fff",
      "& fieldset": { borderColor: T.border },
      "&:hover fieldset": { borderColor: "#9ca3af" },
      "&.Mui-focused fieldset": { borderColor: T.blue },
    },
    "& .MuiInputLabel-root": { color: T.muted },
    "& .MuiInputLabel-root.Mui-focused": { color: T.blue },
  };

  // Pulls the full batch (no pagination) for export
  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await fetch(
        `${API_ENDPOINT}/voice/get-scheduled-voice-report-export/${id}?${buildQS()}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      const json = await res.json();
      const rows = json.data || [];

      if (rows.length === 0) {
        alert("No records found to export.");
        setExporting(false);
        return;
      }

      const ws = XLSX.utils.json_to_sheet(
        rows.map((r, i) => ({
          "#": i + 1,
          Mobile: r.mobile,
          Campaign: r.campaign_name,
          "CLI (Caller ID)": r.cli,
          "Answer Status": r.answer_status,
          "DTMF Key": r.dtmf || "",
          "Call Duration (billing)": fmtDur(r.billing_duration),
          "Patch Duration (s)": r.patch_duration || "",
          "Start Time": r.start_time,
          "Answer Time": r.answer_time,
          "End Time": r.end_time,
          "Hangup Cause": r.hangup_cause,
          "Agent Number": r.agent_number,
          "Agent Answer Time": r.agent_answer_time,
          "Bridge Hangup Cause": r.bridge_hangup_cause,
          "Retry Count": r.retry_count,
          "Recording URL": r.recording_file,
          "Created On": r.createdOn
            ? new Date(r.createdOn).toLocaleString("en-IN")
            : "",
        }))
      );
      ws["!cols"] = [
        4, 14, 22, 16, 14, 10, 20, 18, 20, 20, 20, 22, 16, 20, 20, 12, 50, 22,
      ].map((w) => ({ wch: w }));

      const wsSummary = XLSX.utils.aoa_to_sheet([
        ["Scheduled Batch Voice Report"],
        ["Generated On", new Date().toLocaleString("en-IN")],
        ["Batch Name", json.campaignBatchName || ""],
        ["Total Records", rows.length],
        [],
        ["Answer Status", "Count"],
        ...Object.entries(
          rows.reduce((acc, r) => {
            acc[r.answer_status] = (acc[r.answer_status] || 0) + 1;
            return acc;
          }, {})
        ).map(([k, v]) => [k, v]),
      ]);
      wsSummary["!cols"] = [{ wch: 22 }, { wch: 28 }];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Voice Report");
      XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");
      XLSX.writeFile(
        wb,
        `scheduled-batch-report-${new Date().toISOString().slice(0, 10)}.xlsx`
      );
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box
      sx={{
        mt: 3,
        p: { xs: 1.5, md: 3 },
        background: T.bg,
        minHeight: "100vh",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{ fontWeight: 800, color: T.text, letterSpacing: "-0.5px" }}
          >
            {stats.campaignBatchName || "Scheduled Batch Report"}
          </Typography>
          <Typography variant="body2" sx={{ color: T.muted, mt: 0.4 }}>
            {stats.totalCalls ?? 0} total calls · {campaignNames.length}{" "}
            campaigns in batch
          </Typography>
          {campaignNames.length > 0 && (
            <Stack
              direction="row"
              spacing={0.7}
              flexWrap="wrap"
              useFlexGap
              sx={{ mt: 1 }}
            >
              {campaignNames.map((name) => (
                <Chip
                  key={name}
                  label={name}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: 11,
                    background: T.blueBg,
                    color: T.blue,
                    border: `1px solid ${T.blueBorder}`,
                    fontWeight: 600,
                  }}
                />
              ))}
            </Stack>
          )}
        </Box>

        <Button
          variant="contained"
          size="small"
          startIcon={exporting ? null : <DownloadIcon sx={{ fontSize: 17 }} />}
          onClick={handleExport}
          disabled={exporting}
          sx={{
            background: T.green,
            color: "#fff",
            textTransform: "none",
            fontWeight: 600,
            fontSize: 13,
            px: 2,
            boxShadow: "none",
            "&:hover": { background: "#15803d", boxShadow: "none" },
            "&.Mui-disabled": { background: T.borderLight, color: T.subtle },
          }}
        >
          {exporting ? "Exporting…" : "Export Excel"}
        </Button>
      </Box>

      {/* Filters (no campaign selector — batch is fixed) */}
      <Card sx={{ ...VoiceCardStyle, mb: 3 }}>
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            borderBottom: `1px solid ${T.borderLight}`,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <FilterListIcon sx={{ fontSize: 16, color: T.muted }} />
          <Typography
            sx={{ fontSize: 13, fontWeight: 600, color: T.textSecondary }}
          >
            Filters
          </Typography>
          {activeFilterCount > 0 && (
            <Chip
              label={`${activeFilterCount} active`}
              size="small"
              sx={{
                height: 18,
                fontSize: 11,
                background: T.blueBg,
                color: T.blue,
                border: `1px solid ${T.blueBorder}`,
                fontWeight: 600,
              }}
            />
          )}
        </Box>
        <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search mobile number…"
                value={filters.search}
                onChange={(e) => setF("search", e.target.value)}
                sx={inputSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: T.subtle }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Autocomplete
                size="small"
                options={["Answered", "Not Answered", "Busy", "Failed"]}
                value={filters.answer_status || null}
                onChange={(e, v) => setF("answer_status", v || "")}
                renderInput={(params) => (
                  <TextField {...params} label="Answer Status" sx={inputSx} />
                )}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Autocomplete
                size="small"
                options={["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"]}
                value={filters.dtmf || null}
                onChange={(e, v) => setF("dtmf", v || "")}
                renderInput={(params) => (
                  <TextField {...params} label="DTMF Key" sx={inputSx} />
                )}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={1.5}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From"
                value={filters.startDate}
                onChange={(e) => setF("startDate", e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={1.5}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To"
                value={filters.endDate}
                onChange={(e) => setF("endDate", e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={inputSx}
              />
            </Grid>
            {activeFilterCount > 0 && (
              <Grid item xs={12} md="auto">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={clearFilters}
                  sx={{
                    color: T.muted,
                    borderColor: T.border,
                    textTransform: "none",
                    fontSize: 13,
                    "&:hover": {
                      borderColor: "#9ca3af",
                      background: T.headerBg,
                    },
                  }}
                >
                  Clear all
                </Button>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Stat Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statCards.map((s) => (
          <Grid item xs={6} sm={4} md={12 / 5} key={s.label}>
            <VoiceReportStatCard {...s} loading={false} />
          </Grid>
        ))}
      </Grid>

      {/* Charts */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ ...VoiceCardStyle, height: 300 }}>
            <CardContent
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                p: "16px 18px",
                "&:last-child": { pb: "16px" },
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  color: T.muted,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.7,
                  mb: 0.5,
                }}
              >
                Answer Rate
              </Typography>
              <Box sx={{ flex: 1, position: "relative" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="46%"
                      innerRadius={58}
                      outerRadius={82}
                      paddingAngle={3}
                      dataKey="value"
                      strokeWidth={0}
                    >
                      {pieData.map((e) => (
                        <Cell key={e.name} fill={e.color} />
                      ))}
                    </Pie>
                    <RTooltip {...VoiceTooltipStyle} />
                  </PieChart>
                </ResponsiveContainer>
                <Box
                  sx={{
                    position: "absolute",
                    top: "40%",
                    left: "50%",
                    transform: "translate(-50%,-50%)",
                    textAlign: "center",
                    pointerEvents: "none",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 24,
                      fontWeight: 800,
                      color: T.text,
                      lineHeight: 1,
                    }}
                  >
                    {stats.answerRate ?? 0}%
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 10,
                      color: T.muted,
                      mt: 0.3,
                      fontWeight: 500,
                    }}
                  >
                    answer rate
                  </Typography>
                </Box>
              </Box>
              <Stack
                direction="row"
                spacing={2}
                justifyContent="center"
                sx={{ mt: 0.5 }}
              >
                {pieData.map((e) => (
                  <Box
                    key={e.name}
                    sx={{ display: "flex", alignItems: "center", gap: 0.6 }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: e.color,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{ fontSize: 11, color: T.muted, fontWeight: 500 }}
                    >
                      {e.name}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card sx={{ ...VoiceCardStyle, height: 300 }}>
            <CardContent
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                p: "16px 18px",
                "&:last-child": { pb: "16px" },
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  color: T.muted,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.7,
                  mb: 1,
                }}
              >
                Daily Volume
              </Typography>
              <Box sx={{ flex: 1 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={stats.byDate || []}
                    margin={{ top: 4, right: 16, left: -24, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={T.borderLight}
                    />
                    <XAxis
                      dataKey="date"
                      tick={{ fill: T.subtle, fontSize: 10 }}
                      tickFormatter={(d) => d?.slice(5)}
                    />
                    <YAxis tick={{ fill: T.subtle, fontSize: 10 }} />
                    <RTooltip {...VoiceTooltipStyle} />
                    <Legend wrapperStyle={{ fontSize: 11, color: T.muted }} />
                    <Line
                      type="monotone"
                      dataKey="total"
                      stroke={T.blue}
                      strokeWidth={2.5}
                      dot={false}
                      name="Total"
                    />
                    <Line
                      type="monotone"
                      dataKey="answered"
                      stroke={T.green}
                      strokeWidth={2.5}
                      dot={false}
                      name="Answered"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ ...VoiceCardStyle, height: 300 }}>
            <CardContent
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                p: "16px 18px",
                "&:last-child": { pb: "16px" },
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  color: T.muted,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.7,
                  mb: 1,
                }}
              >
                DTMF Keys Pressed
              </Typography>
              <Box sx={{ flex: 1 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={stats.byDtmf || []}
                    margin={{ top: 4, right: 8, left: -24, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={T.borderLight}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="dtmf"
                      tick={{
                        fill: T.textSecondary,
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    />
                    <YAxis tick={{ fill: T.subtle, fontSize: 10 }} />
                    <RTooltip {...VoiceTooltipStyle} />
                    <Bar
                      dataKey="count"
                      fill={T.purple}
                      radius={[4, 4, 0, 0]}
                      name="Calls"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Table */}
      <VoiceCallLogsTable
        rows={list}
        loading={false}
        page={page}
        totalPages={stats.list?.totalPages}
        totalItems={stats.list?.totalItems}
        onPageChange={(v) => setPage(v)}
      />
    </Box>
  );
};

export default ScheduledVoiceReport;
